import moment from 'moment';
import type { IEventLandingItem } from '../queries/useEventsLanding';

/**
 * Extracts a timezone-aware date key (YYYY-MM-DD) for an event.
 * Uses the event's timezone to determine which calendar day it belongs to,
 * matching how EventCard displays the date via Intl.DateTimeFormat.
 * The event's timezone is the source of truth since that's where the event physically happens.
 *
 * @param formatterCache - shared cache of Intl.DateTimeFormat instances by timezone,
 *   avoids redundant allocations when processing multiple events in the same timezone
 */
export function getEventDateKey(event: IEventLandingItem, formatterCache: Map<string, Intl.DateTimeFormat>): string {
	if (event.timeZone) {
		const date = new Date(event.startTime);
		if (!formatterCache.has(event.timeZone)) {
			formatterCache.set(
				event.timeZone,
				new Intl.DateTimeFormat('en-US', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					timeZone: event.timeZone,
				})
			);
		}
		const parts = formatterCache.get(event.timeZone)!.formatToParts(date);
		const year = parts.find(p => p.type === 'year')?.value;
		const monthValue = parts.find(p => p.type === 'month')?.value;
		const day = parts.find(p => p.type === 'day')?.value;
		return `${year}-${monthValue}-${day}`;
	}
	// No timezone configured — falls back to browser-local date. Events without a
	// timeZone will appear on different calendar days for viewers in different timezones.
	// This is acceptable since EventCard also falls back to browser-local display.
	return moment(event.startTime).format('YYYY-MM-DD');
}
